import {
  ChangeEvent,
  cloneElement,
  KeyboardEvent,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styled from 'styled-components';

import { AudioRecorderButton } from '@components/audio-recorder-button';
import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { FileIconButton } from '@components/file-icon-button';
import { IconButton } from '@components/icon-button';
import { IconAiGradient } from '@components/icons/ai-gradient';
import { IconArrowUp } from '@components/icons/arrow-up';
import { IconBuilder } from '@components/icons/builder';
import { IconCheckboxChecked } from '@components/icons/checkbox-checked';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconDropside } from '@components/icons/dropside';
import { IconFile } from '@components/icons/file';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconTrash } from '@components/icons/trash';
import { SizedRelativeSpinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useStickyAutoScroll } from '@hooks/use-sticky-auto-scroll';
import { useScopedTranslate } from '@locale/translate';
import { useSelectionStore } from '@stores/create-selection-store';
import { theme } from '@styles/theme';
import { IMAGE_EXTENSIONS } from '@utils/assets';
import { pxToRem } from '@utils/converters';
import { uuid } from '@utils/uuid';

import {
  useDeleteAIBuilderHistory,
  useInterruptAIBuilder,
  useRunAIBuilder,
  useTranscribeAudio,
} from 'api/ai-builder/hooks';
import { useWebSocket } from 'api/hooks';
import { useInvalidateJourneyActions } from 'api/journey-actions/hooks';
import { useInvalidateJourneys } from 'api/journeys/hooks';
import { actionLabels } from 'ui/actions/action-label';

import {
  isStatusEvent,
  isToolCallEvent,
  useAIBuilderChat,
  useAutoResizeTextarea,
  type ChatMessage,
  type ChatMessageRole,
  type ToolStatus,
} from './hooks';

import { useNodes } from '../hooks/use-nodes';
import type { JourneyNode } from '../node-types/types';

const DEFAULT_INPUT_ROW_HEIGHT = 72;

type PendingFile = {
  id: string;
  file: File;
};

export const AiBuilderChat = ({
  journeyShortId,
  onClose,
}: {
  journeyShortId: string;
  onClose?: () => void;
}) => {
  const {
    chatId,
    messages: hydrationMessages,
    clearChat,
    isGenerating,
    reduceByWsEvent,
    startRequest,
    endRequest,
  } = useAIBuilderChat({ journeyShortId });
  const { t } = useScopedTranslate(locale);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const messagesInnerRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const inputRowRef = useRef<HTMLDivElement | null>(null);
  const buttonsRowRef = useRef<HTMLDivElement | null>(null);
  const activeToolCallIdsRef = useRef<Set<string>>(new Set());

  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputRowHeight, setInputRowHeight] = useState(DEFAULT_INPUT_ROW_HEIGHT);
  const [showThinking, setShowThinking] = useState(false);
  const [showBuilding, setShowBuilding] = useState(false);
  const [isBuilding, setIsBuilding] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<PendingFile[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const {
    onScroll: handleScroll,
    resetSticky,
    pauseStickyForFrame,
  } = useStickyAutoScroll({
    containerRef: messagesRef,
    contentRef: messagesInnerRef,
    resetKey: chatId,
  });

  const runPrompt = useRunAIBuilder();
  const interruptPrompt = useInterruptAIBuilder();
  const { invalidateJourneyActions } = useInvalidateJourneyActions();
  const { invalidateJourney } = useInvalidateJourneys();
  const deleteHistoryMutation = useDeleteAIBuilderHistory(journeyShortId, chatId!);
  const { selectedNode } = useNodes();

  const resetToIdleState = useCallback(() => {
    activeToolCallIdsRef.current.clear();
    setShowThinking(false);
    setShowBuilding(false);
    setIsBuilding(false);
    endRequest();
  }, [endRequest]);

  const handleInterruptRequest = useCallback(() => {
    if (!chatId) return;
    interruptPrompt.mutate({ journeyShortId, chatId });
    resetToIdleState();
  }, [interruptPrompt, journeyShortId, chatId, resetToIdleState]);

  const appendUserMessage = useCallback(async (text: string, fileNames: string[] = []) => {
    const userMessage: ChatMessage = {
      id: uuid(),
      role: 'user',
      text,
      files: fileNames,
    };

    setMessages((allMessages) => [...allMessages, userMessage]);
  }, []);

  const handleClearUploadedFiles = useCallback(() => {
    setUploadedFiles([]);
    setFileError(null);
  }, []);

  const handleFileSelected = useCallback(
    (file: File) => {
      if (!file) return;

      setFileError(null);

      const match = /\.([^.]+)$/.exec(file.name);
      const extension = match ? `.${match[1].toLowerCase()}` : '';
      if (!IMAGE_EXTENSIONS.includes(extension)) {
        setFileError(t('onlyImagesSupported', { extension }));
        return;
      }

      setUploadedFiles((currentFiles) => {
        const hasDuplicate = currentFiles.some(
          ({ file: existing }) =>
            existing.name === file.name &&
            existing.size === file.size &&
            existing.lastModified === file.lastModified
        );

        if (hasDuplicate) {
          return currentFiles;
        }

        return [...currentFiles, { id: uuid(), file }];
      });
    },
    [t]
  );

  const handleRemoveUploadedFile = useCallback((fileId: string) => {
    setUploadedFiles((currentFiles) => currentFiles.filter((item) => item.id !== fileId));
  }, []);

  useEffect(() => {
    if (hydrationMessages && hydrationMessages.length > 0) {
      setMessages((currentMessages) => {
        if (currentMessages.length === 0) {
          return hydrationMessages as ChatMessage[];
        }
        return currentMessages;
      });
    }
  }, [hydrationMessages]);

  const clearConversation = useCallback(async () => {
    await deleteHistoryMutation.mutateAsync(undefined, {
      onSuccess: () => {
        clearChat();
        setMessages([]);
        resetSticky();
        handleClearUploadedFiles();
      },
    });
  }, [deleteHistoryMutation, clearChat, resetSticky, handleClearUploadedFiles]);

  const wsPath = useMemo(() => `ai-builder/${journeyShortId}/`, [journeyShortId]);

  useWebSocket(
    wsPath,
    (event: MessageEvent<string>) => {
      if (!event?.data) return;

      const envelope = JSON.parse(event.data);
      if (!envelope || envelope.type !== 'update' || !envelope.payload) return;

      const payload = envelope.payload;

      setMessages((allMessages) => reduceByWsEvent(allMessages, payload));

      if (isStatusEvent(payload)) {
        if (['error', 'interrupted', 'completed'].includes(payload.event)) {
          resetToIdleState();
        }
        return;
      }

      if (isToolCallEvent(payload)) {
        const toolPayload = payload;

        if (toolPayload.event === 'start') {
          setShowThinking(false);
          setShowBuilding(true);
          activeToolCallIdsRef.current.add(toolPayload.callId);
          setIsBuilding(true);
        } else if (toolPayload.event === 'end') {
          activeToolCallIdsRef.current.delete(toolPayload.callId);
          if (
            ['create_action', 'edit_action', 'delete_action', 'move_action'].includes(
              toolPayload?.toolType ?? ''
            )
          ) {
            if (!toolPayload.hasError) {
              invalidateJourneyActions(journeyShortId);
              invalidateJourney(journeyShortId);
            }
          }
        }
      } else {
        const assistantMessage = payload;
        if (assistantMessage.event === 'start') {
          setShowThinking(false);
        }
      }
    },
    {
      enabled: Boolean(wsPath),
      throttleTime: 0,
    }
  );

  const { textAreaRef, resizeTextarea } = useAutoResizeTextarea({
    containerRef: contentRef,
    inputRowRef: inputRowRef,
    buttonsRowRef: buttonsRowRef,
    inputValue: chatInput,
    onInputRowHeightChange: setInputRowHeight,
  });

  const handleTranscriptionComplete = useCallback(
    (text: string) => {
      const trimmedText = text.trimEnd();
      setChatInput((currentInput) =>
        currentInput ? `${currentInput} ${trimmedText}` : trimmedText
      );
      requestAnimationFrame(() => {
        resizeTextarea();
        textAreaRef.current?.focus();
      });
    },
    [resizeTextarea, textAreaRef]
  );

  const handleTranscriptionError = useCallback((error: string) => {
    setFileError(error);
  }, []);

  const handleInputSubmit = useCallback(
    (value: string) => {
      const files = uploadedFiles.map((item) => item.file);
      const fileNames = uploadedFiles.map((item) => item.file.name);

      appendUserMessage(value, fileNames);
      startRequest();

      runPrompt.mutate(
        {
          input: value,
          journeyShortId: journeyShortId,
          chatId,
          files,
          selectedAction: selectedNode
            ? { type: selectedNode.type, id: selectedNode.id }
            : undefined,
        },
        {
          onError: () => {
            resetToIdleState();
          },
        }
      );

      setShowBuilding(false);
      setIsBuilding(false);
      activeToolCallIdsRef.current.clear();
      setShowThinking(true);

      setChatInput('');
      handleClearUploadedFiles();
      requestAnimationFrame(() => {
        resizeTextarea();
        textAreaRef.current?.focus();
      });
    },
    [
      chatId,
      appendUserMessage,
      startRequest,
      runPrompt,
      journeyShortId,
      resetToIdleState,
      resizeTextarea,
      textAreaRef,
      handleClearUploadedFiles,
      uploadedFiles,
      selectedNode,
    ]
  );

  return (
    <Container>
      <Header
        onClose={onClose}
        onClearConversation={clearConversation}
        isGenerating={isGenerating}
      />
      <Content ref={contentRef}>
        <Messages
          messages={messages}
          showThinking={showThinking}
          showBuilding={showBuilding}
          isBuilding={isBuilding}
          messagesRef={messagesRef}
          messagesInnerRef={messagesInnerRef}
          handleScroll={handleScroll}
          inputRowHeight={inputRowHeight}
          pauseStickyForFrame={pauseStickyForFrame}
        />
      </Content>
      <InputBox
        value={chatInput}
        onChange={setChatInput}
        onSubmit={handleInputSubmit}
        isDisabled={runPrompt.isLoading && !isGenerating}
        isGenerating={isGenerating}
        onInterrupt={handleInterruptRequest}
        textAreaRef={textAreaRef}
        inputRowRef={inputRowRef}
        buttonsRowRef={buttonsRowRef}
        onResize={resizeTextarea}
        files={uploadedFiles}
        onFileSelected={handleFileSelected}
        onFileRemove={handleRemoveUploadedFile}
        errorText={fileError || undefined}
        onClearError={() => setFileError(null)}
        selectedNode={selectedNode}
        onTranscriptionComplete={handleTranscriptionComplete}
        onTranscriptionError={handleTranscriptionError}
      />
    </Container>
  );
};

const Header = ({
  onClose,
  onClearConversation,
  isGenerating,
}: {
  onClose?: () => void;
  onClearConversation: () => void;
  isGenerating: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  return (
    <StyledHeader>
      <Stack space={2} orientation="horizontal" align="center">
        <IconAiGradient />
        <Text font="bold18" text={t('aiBuilder')} />
      </Stack>
      <Stack space={4} orientation="horizontal" align="center">
        <Menu
          buttonProps={{
            variant: 'ghost',
            size: 'sm',
            iconLeft: <IconMoreVertical />,
          }}
        >
          <Menu.Item
            label={t('clearConversation')}
            variant="danger"
            onSelect={onClearConversation}
            isDisabled={isGenerating}
          />
        </Menu>
        <IconCloseRegular onClick={onClose} color="purple50" style={{ cursor: 'pointer' }} />
      </Stack>
    </StyledHeader>
  );
};

type MessagesProps = {
  messages: ChatMessage[];
  showThinking: boolean;
  showBuilding: boolean;
  isBuilding: boolean;
  messagesRef: RefObject<HTMLDivElement>;
  messagesInnerRef: RefObject<HTMLDivElement>;
  handleScroll: () => void;
  inputRowHeight: number;
  pauseStickyForFrame: () => void;
};

const Messages = ({
  messages,
  showThinking,
  showBuilding,
  isBuilding,
  messagesRef,
  messagesInnerRef,
  inputRowHeight,
  handleScroll,
  pauseStickyForFrame,
}: MessagesProps) => {
  const { t } = useScopedTranslate(locale);
  const { toggle: toggleDescription, isSelected: hasDescriptionOpen } = useSelectionStore();

  const bottomOffsetPx = messages.length === 0 ? DEFAULT_INPUT_ROW_HEIGHT : inputRowHeight;
  const bottomOffset = pxToRem(bottomOffsetPx + 24);

  return (
    <MessagesContainer
      ref={messagesRef}
      onScroll={handleScroll}
      style={{
        bottom: bottomOffset,
        justifyContent: messages.length === 0 ? 'center' : 'flex-start',
      }}
    >
      <div ref={messagesInnerRef}>
        {messages.length === 0 ? (
          <EmptyStateContainer>
            <EmptyState
              header={t('aiBuilder')}
              content={t('description')}
              orientation="vertical"
              variant="small"
              transparent
              icon={<IconAiGradient />}
              iconShadow={cloneElement(<IconAiGradient />, { opacity: 0.1, size: 240 })}
            />
          </EmptyStateContainer>
        ) : (
          <>
            {messages.map((message, idx) => {
              const { role, id, text } = message;
              const isDescriptionOpen = hasDescriptionOpen(id);
              const prevIsTool = idx > 0 && messages[idx - 1]?.role === 'tool';
              const nextIsTool = idx < messages.length - 1 && messages[idx + 1]?.role === 'tool';
              return (
                <div
                  key={`row-${id}`}
                  style={{
                    marginTop: idx === 0 ? 0 : prevIsTool && role === 'tool' ? 0 : pxToRem(8),
                  }}
                >
                  {role === 'assistant' &&
                    (message.errorMessage && message.errorType ? (
                      <MessageContainer $role={role} $isError={true}>
                        <Stack orientation="horizontal" space={2}>
                          <IconFormIndicatorWarning />
                          <Text color="elementOrange200" font="regular14" text={text} />
                        </Stack>
                      </MessageContainer>
                    ) : (
                      <AssistantBorder>
                        <MessageContainer $role={role}>
                          <Text color="textPrimary" font="regular14" text={text} />
                        </MessageContainer>
                      </AssistantBorder>
                    ))}

                  {role === 'tool' && (
                    <ToolGroup>
                      <ToolRow>
                        <ToolRailColumn>
                          <ToolIconWrapper>
                            {message.status === 'done' ? (
                              <IconCheckmark color="elementGreen100" />
                            ) : message.status === 'error' ? (
                              <IconCloseRegular color="elementRed100" />
                            ) : (
                              <SizedRelativeSpinner size={16} $skipMargin />
                            )}
                          </ToolIconWrapper>
                          {nextIsTool && <ToolRailLine />}
                        </ToolRailColumn>
                        <ToolContent>
                          <ToolHeader>
                            <Text font="semibold12" text={message.toolLabel} />
                            {(message.toolDescription || message.errorMessage) && (
                              <IconDropside
                                color="buttonIconPositive"
                                cursor="pointer"
                                rotateDegrees={isDescriptionOpen ? 90 : 0}
                                onClick={(e) => {
                                  e.stopPropagation?.();
                                  pauseStickyForFrame();
                                  toggleDescription(id);
                                }}
                              />
                            )}
                          </ToolHeader>
                        </ToolContent>
                      </ToolRow>

                      {isDescriptionOpen && (message.toolDescription || message.errorMessage) && (
                        <ToolRow>
                          <ToolRailColumn>{nextIsTool && <ToolRailLine />}</ToolRailColumn>
                          <ToolContent>
                            <ToolDescription>
                              <Text
                                font="regular12"
                                text={message.errorMessage || message.toolDescription}
                                color={message.errorMessage ? 'elementRed100' : 'textPrimary'}
                              />
                            </ToolDescription>
                          </ToolContent>
                        </ToolRow>
                      )}

                      {nextIsTool && (
                        <ConnectorGapRow>
                          <ToolRailColumn>
                            <ToolRailLine />
                          </ToolRailColumn>
                          <div style={{ flex: 1 }} />
                        </ConnectorGapRow>
                      )}
                    </ToolGroup>
                  )}

                  {role === 'user' && (
                    <Stack space={2}>
                      <MessageContainer $role={role}>
                        <Text color="elementBlue100" font="regular14" text={text} />
                      </MessageContainer>
                      {Array.isArray(message.files) &&
                        message.files.map((name: string) => (
                          <FileTag key={`${id}-${name}`} name={name} />
                        ))}
                    </Stack>
                  )}
                </div>
              );
            })}
            {showThinking && (
              <StatusRow>
                <AssistantBorder>
                  <MessageContainer $role="assistant">
                    <Stack orientation="horizontal" justify="center" align="center" space={2}>
                      <SizedRelativeSpinner size={24} $skipMargin />
                      <Text aiGradient font="regular14" text={t('thinking')} />
                    </Stack>
                  </MessageContainer>
                </AssistantBorder>
              </StatusRow>
            )}
            {showBuilding && (
              <StatusRow>
                <MessageContainer $role="tool">
                  <Stack orientation="horizontal" justify="space-between" align="center">
                    <Stack orientation="horizontal" justify="center" align="center" space={2}>
                      <IconBuilder color="elementPurple80" />
                      <Text font="semibold12" text={t('buildingWorkflow')} />
                    </Stack>
                    {isBuilding ? (
                      <SizedRelativeSpinner size={16} $skipMargin />
                    ) : (
                      <IconCheckmark color="elementGreen100" />
                    )}
                  </Stack>
                </MessageContainer>
              </StatusRow>
            )}
          </>
        )}
      </div>
    </MessagesContainer>
  );
};

type InputBoxProps = {
  value: string;
  isDisabled?: boolean;
  isGenerating?: boolean;
  placeholder?: string;
  textAreaRef: RefObject<HTMLTextAreaElement>;
  inputRowRef: RefObject<HTMLDivElement>;
  buttonsRowRef: RefObject<HTMLDivElement>;
  onChange: (value: string) => void;
  onSubmit: (value: string) => void;
  onInterrupt?: () => void;
  onResize: () => void;
  files: PendingFile[];
  onFileSelected: (file: File) => void;
  onFileRemove: (fileId: string) => void;
  errorText?: string;
  onClearError?: () => void;
  selectedNode?: JourneyNode;
  onTranscriptionComplete: (text: string) => void;
  onTranscriptionError: (error: string) => void;
};

export const InputBox = ({
  value,
  placeholder,
  textAreaRef,
  inputRowRef,
  buttonsRowRef,
  onChange,
  onSubmit,
  onResize,
  files,
  onFileSelected,
  onFileRemove,
  isDisabled = false,
  isGenerating = false,
  onInterrupt,
  errorText,
  onClearError,
  selectedNode,
  onTranscriptionComplete,
  onTranscriptionError,
}: InputBoxProps) => {
  const { t } = useScopedTranslate(locale);
  const transcriptionMutation = useTranscribeAudio();

  useEffect(() => {
    requestAnimationFrame(onResize);
  }, [files, errorText, selectedNode, onResize]);

  const selectedActionText = useMemo(() => {
    if (!selectedNode) {
      return;
    }

    const label = actionLabels[selectedNode.type];
    return t('selectedAction', { action: label });
  }, [selectedNode, t]);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isGenerating) {
        onInterrupt?.();
        return;
      }
      const trimmedValue = value.trim();
      if (trimmedValue && !isDisabled) {
        onSubmit(trimmedValue);
      }
    }
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    onChange(e.target.value);
    requestAnimationFrame(onResize);
    onClearError?.();
  };

  const handleSubmitClick = () => {
    if (isGenerating) {
      onInterrupt?.();
      return;
    }

    const trimmedValue = value.trim();
    if (trimmedValue && !isDisabled) {
      onSubmit(trimmedValue);
      requestAnimationFrame(() => textAreaRef.current?.focus());
    }
  };

  return (
    <InputRow ref={inputRowRef} onKeyDown={handleKeyDown}>
      <InputTextArea
        ref={textAreaRef}
        value={value}
        onChange={handleChange}
        placeholder={placeholder || t('askAnything')}
        rows={1}
      />
      <Stack space={2} ref={buttonsRowRef}>
        {selectedActionText && (
          <SelectedActionContainer>
            <Text font="regular12" color="textHelper" text={selectedActionText} />
          </SelectedActionContainer>
        )}
        {(files.length > 0 || errorText) && (
          <Stack space={2} style={{ width: '100%' }}>
            {errorText && <Text color="elementRed100" font="regular12" text={errorText} />}
            {files.map(({ id, file }) => (
              <FileTag key={id} name={file.name} onRemove={() => onFileRemove(id)} />
            ))}
          </Stack>
        )}
        <Stack orientation="horizontal" space={2} justify="flex-end" align="center">
          <AudioRecorderButton
            onTranscriptionComplete={onTranscriptionComplete}
            onError={onTranscriptionError}
            transcriptionMutation={transcriptionMutation}
            isDisabled={isDisabled}
          />
          <FileIconButton
            key={files.map(({ id }) => id).join('|')}
            fileType="image"
            onFileSelected={onFileSelected}
          />
          <Button
            variant="primary-blue"
            iconRight={
              isGenerating ? <IconCheckboxChecked size={16} color="white100" /> : <IconArrowUp />
            }
            onClick={handleSubmitClick}
          />
        </Stack>
      </Stack>
    </InputRow>
  );
};

type FileTagProps = {
  name: string;
  onRemove?: () => void;
};

const FileTag = ({ name, onRemove }: FileTagProps) => {
  return (
    <StyledFileTag $isRemovable={Boolean(onRemove)}>
      <IconFile color="elementBlue100" size={16} />
      <Text
        font="regular12"
        text={name}
        ellipsisWithTooltip
        style={{ flex: '1 1 auto', minWidth: 0 }}
      />
      {onRemove && (
        <IconButton
          icon={<IconTrash color="buttonIconPositive" />}
          variant="ghost-purple"
          size="sm"
          onClick={onRemove}
        />
      )}
    </StyledFileTag>
  );
};

const StyledFileTag = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 2,
  align: 'center',
})<{ $isRemovable: boolean }>((p) => ({
  border: `1px solid ${theme.color.border10}`,
  borderRadius: pxToRem(8),
  padding: p.$isRemovable ? `${pxToRem(4)} ${pxToRem(8)}` : pxToRem(8),
  width: '100%',
  minWidth: 0,
}));

const Content = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minHeight: 0,
  width: '100%',
  position: 'relative',
});

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: '100%',
  background: theme.color.backgroundLightDark,
  borderRadius: pxToRem(12),
  boxShadow: theme.shadow.medium,
  overflow: 'hidden',
  position: 'relative',
});

const StyledHeader = styled(Stack).attrs({
  orientation: 'horizontal',
  justify: 'space-between',
})({
  borderBottom: `1px solid ${theme.color.border10}`,
  padding: pxToRem(16),
});

const MessagesContainer = styled(Stack).attrs({
  space: 0,
  justify: 'flex-start',
})({
  position: 'absolute',
  left: pxToRem(16),
  right: pxToRem(16),
  top: pxToRem(16),

  overflowY: 'auto',
  scrollbarColor: `${theme.color.uiTrans10} transparent`,
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    width: pxToRem(8),
    height: pxToRem(8),
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.color.uiTrans10,
    borderRadius: pxToRem(8),
  },
});

const StatusRow = styled.div({
  padding: `${pxToRem(8)} 0`,
});

const EmptyStateContainer = styled.div({
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
});

const InputRow = styled(Stack).attrs({
  space: 2,
})({
  minHeight: pxToRem(56),
  borderRadius: pxToRem(8),
  border: `1px solid ${theme.color.elementBlue100}`,
  padding: pxToRem(8),
  paddingLeft: pxToRem(16),
  overflow: 'hidden',
  background: theme.color.backgroundLightDark,
  position: 'absolute',
  left: pxToRem(16),
  right: pxToRem(16),
  bottom: pxToRem(16),
  zIndex: 1,
});

const SelectedActionContainer = styled.div({
  width: '100%',
  minWidth: 0,
});

const InputTextArea = styled.textarea({
  display: 'block',
  width: '100%',
  minHeight: pxToRem(32),
  font: theme.font.regular16,
  lineHeight: 1.4,
  border: 'none',
  outline: 'none',
  background: 'transparent',
  color: theme.color.textPrimary,
  resize: 'none',
  padding: `${pxToRem(8)} ${pxToRem(8)} 0 0`,
  '&::placeholder': {
    color: theme.color.textHelper,
  },
  scrollbarColor: `${theme.color.uiTrans10} transparent`,
  scrollbarWidth: 'thin',
  '&::-webkit-scrollbar': {
    width: pxToRem(8),
    height: pxToRem(8),
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.color.uiTrans10,
    borderRadius: pxToRem(8),
  },
});

const MessageContainer = styled.div<{
  $role: ChatMessageRole;
  $status?: ToolStatus;
  $isError?: boolean;
}>((p) => ({
  width: '100%',
  borderRadius: pxToRem(7),
  padding: pxToRem(12),
  background: p.$isError
    ? theme.color.orangeTrans10
    : p.$role === 'user'
    ? theme.color.blueTrans10
    : p.$role === 'assistant'
    ? theme.color.backgroundLightDark
    : 'transparent',
  border: p.$role === 'tool' ? `1px solid ${theme.color.border10}` : 'none',
}));

const AssistantBorder = styled.div({
  width: '100%',
  borderRadius: pxToRem(8),
  padding: pxToRem(1),
  background: 'linear-gradient(112deg, rgba(99, 214, 250, 0.20) 0%, rgba(224, 57, 206, 0.20) 100%)',
});

const ToolGroup = styled.div({
  display: 'flex',
  flexDirection: 'column',
});

const ToolRow = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'stretch',
  space: 2,
})({});

const ToolRailColumn = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  width: pxToRem(20),
  flexShrink: 0,
  position: 'relative',
});

const ToolRailLine = styled.div({
  width: pxToRem(1),
  flex: '1 1 auto',
  backgroundColor: theme.color.border10,
  minHeight: pxToRem(8),
});

const ToolIconWrapper = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: pxToRem(20),
  height: pxToRem(20),
  flexShrink: 0,
  zIndex: 1,
  backgroundColor: theme.color.backgroundLightDark,
});

const ToolContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flex: '1 1 auto',
  minWidth: 0,
});

const ToolHeader = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 2,
})({
  minHeight: pxToRem(20),
  paddingBottom: pxToRem(8),
});

const ToolDescription = styled.div({
  paddingTop: pxToRem(4),
  paddingBottom: pxToRem(4),
});

const ConnectorGapRow = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'stretch',
  space: 2,
})({
  height: pxToRem(8),
});

const locale = {
  en: {
    aiBuilder: 'AI Builder',
    description: 'Talk to our AI Builder to build or edit your workflow',
    askAnything: 'Ask anything',
    thinking: 'Thinking',
    buildingWorkflow: 'Building workflow',
    clearConversation: 'Clear Conversation',
    onlyImagesSupported: 'Only images are supported. {extension} is not supported.',
    selectedAction: 'Selected action: {action}',
  },
};
